/**
 * Vejledningens byggesten (beslutning 089).
 *
 * Indholdet står som data i guide/content - ét kapitel pr. fil - så det kan rettes
 * uden at røre ved siden. Hvert afsnit har den mindste rolle, der kan bruge det, og
 * evt. det modul, det hører til. Siden viser kun det, brugerens rolle og de tændte
 * moduler giver adgang til.
 */

export type GuideRole = 'Medarbejder' | 'Projektleder' | 'Administrator' | 'Superadmin'

export const ROLES: GuideRole[] = ['Medarbejder', 'Projektleder', 'Administrator', 'Superadmin']

/** Rollerne er kumulative: en projektleder ser også medarbejderens afsnit. */
export const ROLE_RANK: Record<GuideRole, number> = {
  Medarbejder: 1,
  Projektleder: 2,
  Administrator: 3,
  Superadmin: 4
}

export const ROLE_AUDIENCE: Record<GuideRole, string> = {
  Medarbejder: 'Alle',
  Projektleder: 'Projektleder og op',
  Administrator: 'Administrator og op',
  Superadmin: 'Kun Superadmin'
}

/**
 * Den, der LÆSER vejledningen (beslutning 108). Kontor og Lærling passer ikke ind i trappen: kontoret ser
 * projektlederens afsnit plus intranettets administration, og lærlingen ser medarbejderens - uden VPN.
 * Afsnittene selv har stadig kun de fire trappetrin som rolle.
 */
export type ViewerRole = 'Lærling' | 'Medarbejder' | 'Projektleder' | 'Kontor' | 'Administrator' | 'Superadmin'

export const VIEWER_ROLES: ViewerRole[] = ['Lærling', 'Medarbejder', 'Projektleder', 'Kontor', 'Administrator', 'Superadmin']

/** Til "Vis som": hvor højt på trappen rollen står. */
export const VIEWER_RANK: Record<ViewerRole, number> = {
  Lærling: 1,
  Medarbejder: 1,
  Projektleder: 2,
  Kontor: 2,
  Administrator: 3,
  Superadmin: 4
}

/** Ser rollen afsnittet? Samme regel som serveren: kontoret styrer intranettet, lærlingen har ikke VPN. */
export function canView(section: { role: GuideRole; module?: string }, viewer: ViewerRole): boolean {
  if (viewer === 'Lærling' && section.module === 'vpn') return false
  if (viewer === 'Kontor' && section.module?.startsWith('intranet') && section.role === 'Administrator') return true
  return ROLE_RANK[section.role] <= VIEWER_RANK[viewer]
}

export type Tone = 'info' | 'warn' | 'danger' | 'ok'

/**
 * En byggeklods i en skitse. `n` sætter et nummer ud for klodsen, som trinene
 * henviser til med samme nummer.
 */
export type Block =
  | { t: 'title'; text: string; sub?: string; n?: number }
  | { t: 'text'; text: string; n?: number }
  | { t: 'btn'; text: string; primary?: boolean; danger?: boolean; n?: number }
  | { t: 'btns'; items: { text: string; primary?: boolean; danger?: boolean; n?: number }[] }
  | { t: 'field'; label: string; value?: string; n?: number }
  | { t: 'check'; text: string; checked?: boolean; n?: number }
  | { t: 'pills'; items: string[]; n?: number }
  | { t: 'bar'; value: number; label?: string; n?: number }
  | { t: 'row'; text: string; meta?: string; right?: string; n?: number }
  | { t: 'code'; text: string; n?: number }
  | { t: 'banner'; text: string; tone?: Tone; n?: number }
  | { t: 'photo'; label: string; n?: number }
  | { t: 'table'; cols: string[]; rows?: number; n?: number }
  | { t: 'card'; title?: string; n?: number; children: Block[] }
  | { t: 'cols'; left: Block[]; right: Block[] }
  | { t: 'qr'; label?: string; n?: number }
  | { t: 'sign'; n?: number }

export interface Sketch {
  device: 'phone' | 'desktop' | 'tv'
  /** Overskriften i skitsens topbjælke. */
  title?: string
  /** Vis bundmenuen på en telefonskitse. */
  nav?: boolean
  caption?: string
  blocks: Block[]
}

export interface GuideStep {
  /** Nummeret i skitsen, trinet handler om. */
  n?: number
  text: string
}

export interface GuideFlow {
  title: string
  steps: (string | GuideStep)[]
}

export interface GuideSection {
  id: string
  title: string
  icon: string
  /** Den mindste rolle, der kan bruge afsnittet. */
  role: GuideRole
  /** Modulnøglen (navigation.ts). Er modulet slået fra, vises afsnittet ikke. */
  module?: string
  /** Hvor man finder det i appen. */
  where?: string
  intro: string
  sketch?: Sketch
  flows?: GuideFlow[]
  tips?: string[]
}

export interface GuideChapter {
  id: string
  title: string
  icon: string
  intro: string
  sections: GuideSection[]
}

/** Et trin, der peger på et nummer i skitsen. */
export const n = (num: number, text: string): GuideStep => ({ n: num, text })
